/* ValidateViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ValidateViewRenderer");
const WorkEntity = require("@gluenetworks/platform-sdk-remote").entities.ws.Work;

class ValidateViewComponent extends EventEmitter {
    constructor(planDetails) {
        super();
        this._planDetails = planDetails;
        this._planDetails.childViewOpen = true;
        this._io = this._planDetails._steveIo;
        this._logger = this._planDetails.logger;
        this._gameEditor = this._planDetails.gameEditor;
        this._orgId = this._planDetails._userSession.activeOrganization.id;
        this._plan = this._planDetails.gameEditor.getPlan();
        this._validateDetails = undefined;
        this._workId = undefined;
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._validateInProgress = false;
    }
    whenModelIsReady() {
        return this._gameEditor
            .whenPlanIsReady()
            .then(() => {
                return this._plan.loadRelatedDevices();
            })
            .then((allDevices) => {
                this._gameEditor.disableViewAction("ValidateViewPlan");
                return this._getValidated();
            })
            .then((results) => {
                this._validateDetails = results;
                this._gameEditor.enableViewAction("ValidateViewPlan");
                this._gameEditor.disableViewAction("StopValidation");
                return;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "IoTOsPlanDetailsViewComponent._validatePlan() error."
                );
                return err;
            });
    }

    _getValidated() {
        // Clean up existing validation
        this._workId = undefined;
        if (this._validateLogFeed) {
            this._unregisterLogFeedListeners();
            this._validateLogFeed.shutdown();
            this._validateLogFeed = undefined;
        }

        // Run validation
        return this._plan
            .runValidatePlanWork({}, {}, (state) => {
                // Attempting to start log feed as soon as workId is available
                if (!this._workId && state && state.workId) {
                    const workId = state.workId;
                    const status = state.status;
                    this._planDetails._validateWorkId = workId;
                    this._workId = workId;

                    // Handle async operation properly and catch errors
                    this._getValidationLogs(workId)
                        .then(() => {
                            this._renderer._enableStopValidation();
                            this._renderer._enableDisableValidatePlan(status);
                        })
                        .catch((error) => {
                            this._logger.error(
                                {error, workId},
                                "Failed to initialize log feed during validation"
                            );
                            // Don't rethrow - let validation continue even if log feed fails
                        });
                }
            })
            .then((results) => {
                // If feed is not initialized or has no logs, start log feed (again)
                // TODO: This is a workaround for PE-13060 - Log feed initialized while work is running sometimes fails
                const needsLogFeed =
                    !this._validateLogFeed ||
                    (this._validateLogFeed.getCache &&
                        Object.keys(this._validateLogFeed.getCache()).length === 0);

                if (needsLogFeed) {
                    // Clean up existing feed before creating new one
                    if (this._validateLogFeed) {
                        this._unregisterLogFeedListeners();
                        this._validateLogFeed.shutdown();
                        this._validateLogFeed = undefined;
                    }

                    const workId = results && results.summary && results.summary.workId;
                    const status = results && results.status;

                    if (!workId) {
                        throw new Error("No work ID returned from validation");
                    }

                    this._planDetails._validateWorkId = workId;
                    this._workId = workId;

                    return this._getValidationLogs(workId).then(() => {
                        this._renderer._enableStopValidation();
                        this._renderer._enableDisableValidatePlan(status);
                        return results;
                    });
                }

                return results;
            })
            .catch((error) => {
                this._logger.error({error}, "Failed to run plan validation work");

                // Clean up on error to prevent inconsistent state
                this._workId = undefined;
                if (this._validateLogFeed) {
                    this._unregisterLogFeedListeners();
                    this._validateLogFeed.shutdown();
                    this._validateLogFeed = undefined;
                }

                throw error;
            });
    }

    _getValidationLogs(workId) {
        this._validateLogFeed = WorkEntity.startWorkLogElementFeed(this._orgId, workId);
        this._registerLogFeedListeners();
        return this._validateLogFeed.whenInitialized();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerLogFeedListeners() {
        this._validateLogFeed.on(
            "ElementInitialized",
            (this._onLogAdded = this._onLogAdded.bind(this))
        );
        this._validateLogFeed.on("ElementAdded", (this._onLogAdded = this._onLogAdded.bind(this)));
    }
    _unregisterLogFeedListeners() {
        if (this._validateLogFeed) {
            this._validateLogFeed.removeListener("ElementInitialized", this._onLogAdded);
            this._validateLogFeed.removeListener("ElementAdded", this._onLogAdded);
        }
    }
    _registerListeners() {
        this._plan.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }
    _unregisterListeners() {
        this._plan.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
        this._unregisterLogFeedListeners();
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get plan() {
        return this._plan;
    }
    get logFile() {
        return this.getLogFile();
    }
    getLogFile() {
        return this._logFile;
    }
    get io() {
        return this._io;
    }
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();

        return this.renderer
            ._shutdown()
            .then(() => {
                this._validateDetails = undefined;
                this._planDetails.renderer._validateView = undefined;
                this._validateInProgress = undefined;
                if (this._validateLogFeed) {
                    this._validateLogFeed.shutdown();
                    this._validateLogFeed = undefined;
                }
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err);
            });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onLogAdded(log) {
        this.emit("LogAdded", log);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    stopValidation() {
        this._planDetails._renderer._setViewActionText("ValidateViewPlan", "Validate Plan");
        this._planDetails._renderer._setViewActionText("ValidatePlan", "Validate Plan");
        this._gameEditor.enableViewAction("ValidatePlan");
        this._renderer._enableDownload();
        this._renderer._enableValidatePlan();
        this._renderer._disableStopValidation();
        this._plan.cancelPlanWork(this._workId);
    }

    _onPristinePropertiesChanged(newVal) {
        this._renderer._enableDisableValidatePlan(newVal.status);

        if (newVal.status !== "VALIDATING") {
            this._renderer._disableStopValidation();
        } else {
            this._renderer._enableStopValidation();
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }
    get logger() {
        return this._logger;
    }
    get logs() {
        return this.getLogs();
    }
    getLogs() {
        return this._validateDetails.log;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = ValidateViewComponent;
ValidateViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.io",
];
ValidateViewComponent.$displayDetails = {
    type: "View",
    name: "OS Plan Validation",
    icon: "common.edit",
    shown: false,
};
